import { Tooltip } from '@/components/ui/Tooltip';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { useSessionUser } from '@/hooks/auth/useSessionUser';
import { authActions } from '@/redux/auth/authSlice';
import { clearAuthStorage, logoutAccounts, redirectToSignedOut } from '@/services/auth';
import { ProductSwitcher } from '@/components/ui/ProductSwitcher';
import {
  useCalendarVisibilityPreferences,
  usePatchCalendarVisibilityPreferences,
} from '@/hooks/preferences/useCalendarVisibilityPreferences';

type Props = {
  onRefresh: () => void;
  visibilitySettingsOpen: boolean;
  onToggleVisibilitySettings: () => void;
};

export function CalendarToolbar({
  onRefresh,
  visibilitySettingsOpen,
  onToggleVisibilitySettings,
}: Props) {
  const { user, accessToken } = useSessionUser();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [loggingOut, setLoggingOut] = useState(false);
  const [open, setOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const visibilityQuery = useCalendarVisibilityPreferences();
  const patchVisibility = usePatchCalendarVisibilityPreferences();

  const displayInitial =
    (typeof user?.displayName === 'string' && user.displayName.trim()[0]) ||
    (typeof user?.email === 'string' && user.email.trim()[0]) ||
    '?';

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logoutAccounts(accessToken);
      clearAuthStorage();
      queryClient.clear();
      dispatch(authActions.clearAuth());
      try {
        localStorage.setItem('sculpx_logout', Date.now().toString());
        localStorage.removeItem('sculpx_logout');
      } catch {
        /* ignore */
      }
      redirectToSignedOut();
    } finally {
      setLoggingOut(false);
    }
  };

  useEffect(() => {
    if (!open && !visibilitySettingsOpen) return;
    const onDoc = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      setOpen(false);
      if (visibilitySettingsOpen) onToggleVisibilitySettings();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onToggleVisibilitySettings, visibilitySettingsOpen]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setIsDark(document.documentElement.classList.contains('dark'));
  }, []);

  const toggleTheme = () => {
    if (typeof document === 'undefined') return;
    const next = !document.documentElement.classList.contains('dark');
    document.documentElement.classList.toggle('dark', next);
    setIsDark(next);
  };

  return (
    <div ref={wrapRef} className="relative flex items-center justify-end gap-2">
      <ProductSwitcher />
      <Tooltip label="Settings" side="bottom" disabled={visibilitySettingsOpen}>
      <button
        type="button"
        onClick={onToggleVisibilitySettings}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-600 transition-colors hover:bg-[#E8EAF2] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
        aria-label="Open calendar settings"
      >
        <SettingsIcon />
      </button>
      </Tooltip>
      <Tooltip label={isDark ? 'Light mode' : 'Dark mode'} side="bottom">
      <button
        type="button"
        onClick={toggleTheme}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-600 transition-colors hover:bg-[#E8EAF2] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? <SunIcon /> : <MoonIcon />}
      </button>
      </Tooltip>
      <Tooltip label="Profile" side="bottom" align="end" disabled={open}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#5B6CF6] to-[#8B5CF6] text-sm font-bold text-white transition-transform hover:opacity-95 active:scale-95"
        aria-label="Open profile menu"
      >
        {displayInitial.toUpperCase()}
      </button>
      </Tooltip>

      {open ? (
        <div className="absolute right-0 top-14 z-50 w-64 rounded-3xl border border-slate-100 bg-white p-2 text-[#0f172b] shadow-[0_20px_50px_-20px_rgba(15,23,43,0.25)] animate-in fade-in slide-in-from-top-2 duration-200 dark:border-white/10 dark:bg-card dark:text-slate-100 dark:shadow-black/40">
          <div className="flex items-center gap-3 rounded-2xl bg-[#F6F7FB] p-3 dark:bg-white/5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#5B6CF6] to-[#8B5CF6] text-sm font-bold text-white">
              {displayInitial.toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Account</p>
              <p className="truncate text-sm font-semibold text-[#0f172b] dark:text-white">{user?.displayName || user?.email || 'User'}</p>
            </div>
          </div>

          <div className="mt-2 space-y-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void onRefresh();
              }}
              className="group flex w-full items-center gap-3 rounded-2xl px-2 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-[#EEF2FF] hover:text-[#5B47E8] dark:text-slate-200 dark:hover:bg-[#1C1F3F] dark:hover:text-[#8B7CFF]"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-500 transition-colors group-hover:bg-white group-hover:text-[#5B47E8] dark:bg-white/5 dark:text-slate-400 dark:group-hover:bg-white/10 dark:group-hover:text-[#8B7CFF]">
                <RefreshIcon />
              </span>
              Refresh Data
            </button>

            <button
              type="button"
              onClick={() => void handleLogout()}
              disabled={loggingOut}
              className="group flex w-full items-center gap-3 rounded-2xl px-2 py-2 text-left text-sm font-medium text-rose-600 transition-colors hover:bg-rose-50 disabled:opacity-60 dark:text-rose-400 dark:hover:bg-rose-500/10"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-50 text-rose-500 transition-colors group-hover:bg-white dark:bg-rose-500/10 dark:text-rose-400 dark:group-hover:bg-rose-500/20">
                <LogoutIcon />
              </span>
              {loggingOut ? 'Signing out...' : 'Log out'}
            </button>
          </div>
        </div>
      ) : null}

      {visibilitySettingsOpen ? (
        <div className="absolute right-0 top-14 z-50 w-[340px] rounded-3xl border border-slate-100 bg-white p-5 text-[#0f172b] shadow-[0_20px_50px_-20px_rgba(15,23,43,0.25)] animate-in fade-in slide-in-from-top-2 duration-200 dark:border-white/10 dark:bg-card dark:text-slate-100 dark:shadow-black/40">
          <div className="pb-3">
            <p className="text-lg font-bold tracking-tight text-[#0f172b] dark:text-white">Calendar Settings</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Control what appears in the calendar grid.</p>
          </div>
          <div className="space-y-0.5 rounded-2xl bg-[#F6F7FB] p-2 dark:bg-white/5">
            <VisibilityToggleRow
              label="Events"
              checked={visibilityQuery.data?.showEvents ?? true}
              disabled={patchVisibility.isPending || visibilityQuery.isLoading}
              onChange={(next) => patchVisibility.mutate({ showEvents: next })}
            />
            <VisibilityToggleRow
              label="Tasks"
              checked={visibilityQuery.data?.showTasks ?? true}
              disabled={patchVisibility.isPending || visibilityQuery.isLoading}
              onChange={(next) => patchVisibility.mutate({ showTasks: next })}
            />
            <VisibilityToggleRow
              label="Projects"
              checked={visibilityQuery.data?.showPmsProjects ?? true}
              disabled={patchVisibility.isPending || visibilityQuery.isLoading}
              onChange={(next) => patchVisibility.mutate({ showPmsProjects: next })}
            />
            <VisibilityToggleRow
              label="Notes"
              checked={visibilityQuery.data?.showNotes ?? true}
              disabled={patchVisibility.isPending || visibilityQuery.isLoading}
              onChange={(next) => patchVisibility.mutate({ showNotes: next })}
            />
            <VisibilityToggleRow
              label="Meetings"
              checked={visibilityQuery.data?.showMeetings ?? true}
              disabled={patchVisibility.isPending || visibilityQuery.isLoading}
              onChange={(next) => patchVisibility.mutate({ showMeetings: next })}
            />
            <VisibilityToggleRow
              label="Holidays"
              checked={visibilityQuery.data?.showHolidays ?? true}
              disabled={patchVisibility.isPending || visibilityQuery.isLoading}
              onChange={(next) => patchVisibility.mutate({ showHolidays: next })}
            />
            <VisibilityToggleRow
              label="Org Events"
              checked={visibilityQuery.data?.showOrgEvents ?? true}
              disabled={patchVisibility.isPending || visibilityQuery.isLoading}
              onChange={(next) => patchVisibility.mutate({ showOrgEvents: next })}
            />
            <VisibilityToggleRow
              label="Attendance"
              checked={visibilityQuery.data?.showAttendance ?? false}
              disabled={patchVisibility.isPending || visibilityQuery.isLoading}
              onChange={(next) => patchVisibility.mutate({ showAttendance: next })}
            />
          </div>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-[11px] font-medium text-slate-400">Saved per user</span>
            <button
              type="button"
              onClick={onToggleVisibilitySettings}
              className="rounded-full bg-gradient-to-r from-[#5B6CF6] to-[#8B5CF6] px-5 py-2 text-xs font-semibold text-white transition hover:opacity-95 active:scale-95"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RefreshIcon() { return <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>; }
function LogoutIcon() { return <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>; }
function SettingsIcon() { return <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></svg>; }
function MoonIcon() { return <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>; }
function SunIcon() { return <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v2m0 14v2m9-9h-2M5 12H3m15.364 6.364l-1.414-1.414M7.05 7.05 5.636 5.636m12.728 0L16.95 7.05M7.05 16.95l-1.414 1.414M12 8a4 4 0 100 8 4 4 0 000-8z" /></svg>; }

function VisibilityToggleRow({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-center justify-between rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-white dark:hover:bg-white/5 ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
      <span className="font-medium text-slate-700 dark:text-slate-200">{label}</span>
      <span className="relative inline-flex h-6 w-11 shrink-0 items-center">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span className="absolute inset-0 rounded-full bg-slate-200 transition-colors peer-checked:bg-gradient-to-r peer-checked:from-[#5B6CF6] peer-checked:to-[#8B5CF6] peer-focus-visible:ring-4 peer-focus-visible:ring-[#5B6CF6]/20 dark:bg-white/10" />
        <span className="absolute left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
      </span>
    </label>
  );
}
